/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { createService } from 'src/core/utils/createService';
import { SharePointDriveItem } from 'src/qnaMaker/core/services/sharePoint/qnaMakerSharePointSwaggers';
import { userAgentApplication } from 'src/qnaMaker/core/services/sharePoint/userAgentApplication';

export const getQnaMakerSharePointService = createService(() => {
    const validFileExtensions: string[] = ['.doc', '.docx', '.pdf', '.tsv', '.txt', '.xlsx'];
    const accessTokenRequest = {
        scopes: ['user.read']
    };

    const encodeSharedUrl = (sharingUrl: string) => {
        const base64Value = Buffer.from(sharingUrl).toString('base64');
        const unpaddedBase64 = base64Value
            .replace(/=+$/g, '')
            .replace('/', '_')
            .replace('+', '-');
        const encodedUrl = `u!${unpaddedBase64}`;

        return encodedUrl;
    };

    const getAccessToken = async (): Promise<string> => {
        try {
            await userAgentApplication.loginPopup({ ...accessTokenRequest, prompt: 'select_account' });
        } catch (e) {
            throw new Error('Could not authenticate. Please make sure pop-ups are enabled for this website.');
        }

        const token = await userAgentApplication
            .acquireTokenSilent(accessTokenRequest)
            .then(accessTokenResponse => accessTokenResponse.accessToken)
            .catch(error => {
                if (error.errorMessage.indexOf('interaction_required') !== -1 || error.errorMessage.indexOf('consent_required') !== -1) {
                    userAgentApplication.acquireTokenPopup(accessTokenRequest).then(accessTokenResponse => accessTokenResponse.accessToken);
                }
            });

        return token || '';
    };

    const getSharePointSharedItem = async ({ fileUrl }: { fileUrl: string }): Promise<SharePointDriveItem> => {
        const accessToken = await getAccessToken();

        const apiOptions = luisRequestOptionsBuilder()
            .useCognitiveServicesTokenHeader(accessToken)
            .useContentType('json')
            .build();

        const url: string = `https://graph.microsoft.com/v1.0/shares/${encodeSharedUrl(fileUrl)}/driveItem`;

        try {
            const { data: driveItem } = await httpService.get<SharePointDriveItem>(url, apiOptions);

            const extensionIdx: number = driveItem.name.lastIndexOf('.');
            if (extensionIdx > 0) {
                const extension: string = driveItem.name.substring(extensionIdx).toLowerCase();
                const isValidFileExtension = validFileExtensions.indexOf(extension) > -1;

                if (isValidFileExtension) {
                    return driveItem;
                }
            }

            throw new Error(`Unsupported/Invalid URL(s): ${fileUrl}`);
        } catch (e) {
            throw new Error(`Unsupported/Invalid URL(s): ${fileUrl}`);
        }
    };

    return { getSharePointSharedItem };
});
